'use client';

import { useState, useEffect } from 'react';
import { useCart } from '@/contexts/CartContext';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { getImageUrl } from '@/lib/utils';
import { CartItem, OrderType, OrderOption, DisplayMode } from '@/types';
import EditCartItemModal from '@/components/EditCartItemModal';
import SaveToFavoritesModal from '@/components/SaveToFavoritesModal';
import { authApi, settingsApi, orderOptionsApi } from '@/lib/api';

interface Settings {
  deliveryFee: number;
  street?: string;
  city?: string;
  restaurantName?: string;
  restaurantNameHe?: string;
}

export default function CartPage() {
  const router = useRouter();
  const { cart, removeFromCart, updateQuantity, updateCartItem, clearCart, getCartTotal, getCartItemsCount, setOrderType, customerNote, setCustomerNote, selectedOrderOptions, setSelectedOrderOptions } = useCart();
  const [editingItem, setEditingItem] = useState<{ item: CartItem; index: number } | null>(null);
  const [favoritesItem, setFavoritesItem] = useState<CartItem | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [orderOptions, setOrderOptions] = useState<OrderOption[]>([]);

  useEffect(() => {
    checkAuth();
    loadSettings();
    loadOrderOptions();
  }, []);

  const loadOrderOptions = async () => {
    try {
      const response = await orderOptionsApi.getOrderOptions();
      setOrderOptions(response.data);
    } catch (error) {
      console.error('Failed to load order options:', error);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await settingsApi.getSettings();
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
      // Default fallback
      setSettings({ deliveryFee: 10 });
    }
  };

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('amam_token');
      if (token) {
        await authApi.getProfile();
        setIsAuthenticated(true);
      }
    } catch (error) {
      setIsAuthenticated(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('amam_token');
    localStorage.removeItem('amam_user');
    setIsAuthenticated(false);
    setUserMenuOpen(false);
    router.push('/');
  };

  // Delivery fee only applies for delivery orders
  const deliveryFee = cart.orderType === OrderType.DELIVERY ? (settings?.deliveryFee || 0) : 0;
  const subtotal = getCartTotal();
  const total = subtotal + deliveryFee;

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-white shadow-sm sticky top-0 z-20">
          <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
            {/* Mobile: Line 1 - Back */}
            <div className="flex md:hidden items-center justify-between pb-3 border-b border-gray-100">
              <Link href="/menu" className="text-blue-600 hover:underline">
                → חזור לתפריט
              </Link>
            </div>
            {/* Mobile: Line 2 / Desktop: Single line */}
            <div className="flex items-center justify-between pt-3 md:pt-0">
              <h1 className="text-2xl font-bold text-gray-900">סל הקניות שלך</h1>
              <div className="flex items-center gap-4">
                {/* User Menu */}
                {isAuthenticated ? (
                  <div className="relative">
                    <button
                      onClick={() => setUserMenuOpen(!userMenuOpen)}
                      className="flex items-center gap-2 text-gray-700 hover:text-blue-600 font-medium px-4 py-2 rounded-lg hover:bg-gray-100 transition"
                    >
                      👤 החשבון שלי
                    </button>
                    {userMenuOpen && (
                      <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 border z-[100]">
                        <Link
                          href="/account"
                          className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          לוח בקרה
                        </Link>
                        <Link
                          href="/account/orders"
                          className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          רכישות קודמות
                        </Link>
                        <Link
                          href="/account/addresses"
                          className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          כתובות שלי
                        </Link>
                        <Link
                          href="/account/favorites"
                          className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          מועדפים שלי
                        </Link>
                        <Link
                          href="/account/profile"
                          className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          פרופיל
                        </Link>
                        <hr className="my-2" />
                        <button
                          onClick={handleLogout}
                          className="block w-full text-right px-4 py-2 text-red-600 hover:bg-gray-100"
                        >
                          התנתק
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <Link
                      href="/login"
                      className="text-gray-700 hover:text-blue-600 font-medium px-4 py-2 rounded-lg hover:bg-gray-100 transition"
                    >
                      התחבר
                    </Link>
                    <Link
                      href="/register"
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                    >
                      הירשם
                    </Link>
                  </div>
                )}
                {/* Back to menu - Desktop only */}
                <Link href="/menu" className="hidden md:block text-blue-600 hover:underline">
                  חזור לתפריט
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Empty Cart */}
        <main className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-6xl mb-4">🛒</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">הסל שלך ריק</h2>
            <p className="text-gray-600 mb-6">הוסף מוצרים מהתפריט כדי להתחיל להזמין</p>
            <Link
              href="/menu"
              className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition"
            >
              עבור לתפריט
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          {/* Mobile: Line 1 - Back and Clear Cart */}
          <div className="flex md:hidden items-center justify-between pb-3 border-b border-gray-100">
            <Link href="/menu" className="text-blue-600 hover:underline">
              → חזור לתפריט
            </Link>
            <button
              onClick={clearCart}
              className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition font-medium"
            >
              רוקן סל
            </button>
          </div>
          {/* Mobile: Line 2 / Desktop: Single line */}
          <div className="flex items-center justify-between pt-3 md:pt-0">
            <h1 className="text-2xl font-bold text-gray-900">
              סל הקניות ({getCartItemsCount()} פריטים)
            </h1>
            <div className="flex items-center gap-4">
              {/* User Menu */}
              {isAuthenticated ? (
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 text-gray-700 hover:text-blue-600 font-medium px-4 py-2 rounded-lg hover:bg-gray-100 transition"
                  >
                    👤 החשבון שלי
                  </button>
                  {userMenuOpen && (
                    <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 border z-[100]">
                      <Link
                        href="/account"
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        לוח בקרה
                      </Link>
                      <Link
                        href="/account/orders"
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        רכישות קודמות
                      </Link>
                      <Link
                        href="/account/addresses"
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        כתובות שלי
                      </Link>
                      <Link
                        href="/account/favorites"
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        מועדפים שלי
                      </Link>
                      <Link
                        href="/account/profile"
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        פרופיל
                      </Link>
                      <hr className="my-2" />
                      <button
                        onClick={handleLogout}
                        className="block w-full text-right px-4 py-2 text-red-600 hover:bg-gray-100"
                      >
                        התנתק
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <Link
                    href="/login"
                    className="text-gray-700 hover:text-blue-600 font-medium px-4 py-2 rounded-lg hover:bg-gray-100 transition"
                  >
                    התחבר
                  </Link>
                  <Link
                    href="/register"
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                  >
                    הירשם
                  </Link>
                </div>
              )}
              {/* Clear cart - Desktop only (shown in line 1 on mobile) */}
              <button
                onClick={clearCart}
                className="hidden md:block text-red-600 hover:text-red-700 font-medium"
              >
                רוקן סל
              </button>
              {/* Continue shopping - Desktop only */}
              <Link href="/menu" className="hidden md:block text-blue-600 hover:underline">
                המשך קניה
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map((item, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6">
                {/* Top section: Image and Product Details */}
                <div className="flex gap-4 mb-4">
                  {/* Product Image */}
                  <div className="w-20 h-20 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {item.productImage ? (
                      <img
                        src={getImageUrl(item.productImage)}
                        alt={item.productNameHe || item.productName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-2xl">🍽️</span>
                    )}
                  </div>

                  <div className="flex-1">
                    {/* Product Name */}
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {item.productNameHe || item.productName}
                    </h3>

                    {/* Selected Options */}
                    {item.selectedOptions.length > 0 && (
                      <div className="space-y-1">
                        {item.selectedOptions.map((opt, optIdx) => {
                          const optionValue = opt.textValue || opt.selectedValueHe || opt.selectedValue;
                          const optionLabel = opt.optionNameHe || opt.optionName;

                          return (
                            <div key={optIdx} className="text-sm text-gray-600">
                              {optionValue ? (
                                // Normal case: show option name with value
                                <>
                                  <span className="font-medium">{optionLabel}:</span>{' '}
                                  {optionValue}
                                </>
                              ) : (
                                // Fallback for old data: option name is likely the value itself
                                <span className="font-medium">{optionLabel}</span>
                              )}
                              {opt.quantity && opt.quantity > 1 && ` (x${opt.quantity})`}
                              {opt.priceModifier !== 0 && (
                                <span className="text-gray-500">
                                  {' '}({opt.priceModifier > 0 ? '+' : ''}₪{opt.priceModifier.toFixed(2)})
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Special Instructions */}
                    {item.specialInstructions && (
                      <div className="text-sm text-gray-600 mt-2">
                        <span className="font-medium">הערות:</span> {item.specialInstructions}
                      </div>
                    )}
                  </div>
                </div>

                {/* Bottom section: Quantity Controls, Edit, Remove - Full width */}
                <div className="flex flex-col gap-3 pt-4 border-t border-gray-100">
                  {/* Controls row - Quantity, Edit, Remove */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-700">כמות:</span>
                      <button
                        onClick={() => updateQuantity(index, item.quantity - 1)}
                        className="w-8 h-8 bg-gray-100 rounded-lg font-bold hover:bg-gray-200 transition"
                      >
                        -
                      </button>
                      <span className="w-8 text-center font-semibold">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(index, item.quantity + 1)}
                        className="w-8 h-8 bg-gray-100 rounded-lg font-bold hover:bg-gray-200 transition"
                      >
                        +
                      </button>
                    </div>

                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => setEditingItem({ item, index })}
                        className="text-blue-600 hover:text-blue-700 text-center"
                        aria-label="ערוך פריט"
                      >
                        <span className="inline-block text-3xl">✏️</span>
                        <br />
                        ערוך
                      </button>
                      <button
                        onClick={() => removeFromCart(index)}
                        className="text-red-600 hover:text-red-700 text-center"
                        aria-label="הסר פריט"
                      >
                        <span className="inline-block text-3xl">🗑️</span>
                        <br />
                        הסר
                      </button>
                    </div>
                  </div>

                  {/* Price row with favorites button */}
                  <div className="flex justify-between items-center">
                    <button
                      onClick={() => {
                        if (!isAuthenticated) {
                          alert('יש להתחבר כדי לשמור מועדפים');
                          router.push('/login');
                          return;
                        }
                        setFavoritesItem(item);
                      }}
                      className="px-3 py-2 border border-red-300 text-red-500 rounded-lg hover:bg-red-50 transition flex items-center gap-2 text-sm"
                      title="שמור למועדפים"
                    >
                      ❤️ שמור למועדפים
                    </button>
                    <span className="text-xl font-bold text-blue-600">
                      ₪{item.itemTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            ))}

            {/* Order Options */}
            {orderOptions.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">אפשרויות נוספות</h2>
                <div className="space-y-4">
                  {orderOptions.map(option => (
                    <div key={option._id}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {option.nameHe || option.name}
                        {option.required && <span className="text-red-600 mr-1">*</span>}
                      </label>

                      {option.displayMode === DisplayMode.INLINE_OPTIONS && (
                        <div className="flex flex-wrap gap-2">
                          {option.values.map(value => (
                            <button
                              key={value._id}
                              type="button"
                              onClick={() => setSelectedOrderOptions({
                                ...selectedOrderOptions,
                                [option._id]: value._id,
                              })}
                              className={`px-3 py-2 rounded-lg border-2 transition ${
                                selectedOrderOptions[option._id] === value._id
                                  ? 'border-blue-600 bg-blue-50'
                                  : 'border-gray-200 hover:border-blue-300'
                              }`}
                            >
                              {value.nameHe || value.name}
                            </button>
                          ))}
                        </div>
                      )}

                      {option.displayMode === DisplayMode.TEXT_AREA && (
                        <textarea
                          value={selectedOrderOptions[option._id] || ''}
                          onChange={(e) => setSelectedOrderOptions({
                            ...selectedOrderOptions,
                            [option._id]: e.target.value,
                          })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          rows={3}
                          placeholder={option.placeholderHe || option.placeholder}
                        />
                      )}

                      {option.displayMode === DisplayMode.TEXT_INPUT && (
                        <input
                          type="text"
                          value={selectedOrderOptions[option._id] || ''}
                          onChange={(e) => setSelectedOrderOptions({
                            ...selectedOrderOptions,
                            [option._id]: e.target.value,
                          })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder={option.placeholderHe || option.placeholder}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Customer Note */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">הערות להזמנה</h2>
              <textarea
                value={customerNote}
                onChange={(e) => setCustomerNote(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="הערות נוספות למסעדה (אופציונלי)"
              />
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
              <h2 className="text-xl font-bold text-gray-900 mb-4">סיכום הזמנה</h2>

              {/* Order Type Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">סוג הזמנה</label>
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setOrderType(OrderType.DELIVERY)}
                    className={`w-full p-3 rounded-lg border-2 text-right flex items-center gap-3 transition ${
                      cart.orderType === OrderType.DELIVERY
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <span className="text-2xl">🚗</span>
                    <div className="flex-1">
                      <div className="font-medium">משלוח</div>
                      <div className="text-sm text-gray-500">משלוח עד הבית</div>
                    </div>
                    {cart.orderType === OrderType.DELIVERY && (
                      <span className="text-blue-600 text-xl">✓</span>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => setOrderType(OrderType.TAKE_AWAY)}
                    className={`w-full p-3 rounded-lg border-2 text-right flex items-center gap-3 transition ${
                      cart.orderType === OrderType.TAKE_AWAY
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <span className="text-2xl">🥡</span>
                    <div className="flex-1">
                      <div className="font-medium">לקחת</div>
                      <div className="text-sm text-gray-500">איסוף עצמי מהמסעדה</div>
                    </div>
                    {cart.orderType === OrderType.TAKE_AWAY && (
                      <span className="text-blue-600 text-xl">✓</span>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => setOrderType(OrderType.SIT_IN)}
                    className={`w-full p-3 rounded-lg border-2 text-right flex items-center gap-3 transition ${
                      cart.orderType === OrderType.SIT_IN
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <span className="text-2xl">🍽️</span>
                    <div className="flex-1">
                      <div className="font-medium">ישיבה במקום</div>
                      <div className="text-sm text-gray-500">אכילה במסעדה</div>
                    </div>
                    {cart.orderType === OrderType.SIT_IN && (
                      <span className="text-blue-600 text-xl">✓</span>
                    )}
                  </button>
                </div>
              </div>

              <div className="space-y-3 mb-4">
                <div className="flex justify-between text-gray-600">
                  <span>סכום ביניים</span>
                  <span>₪{subtotal.toFixed(2)}</span>
                </div>
                {cart.orderType === OrderType.DELIVERY && (
                  <div className="flex justify-between text-gray-600">
                    <span>דמי משלוח</span>
                    <span>₪{deliveryFee.toFixed(2)}</span>
                  </div>
                )}
                <div className="border-t pt-3 flex justify-between text-xl font-bold">
                  <span>סה"כ</span>
                  <span className="text-blue-600">₪{total.toFixed(2)}</span>
                </div>
              </div>

              <button
                onClick={() => router.push('/checkout')}
                className="w-full bg-blue-600 text-white py-4 rounded-lg font-bold text-lg hover:bg-blue-700 transition"
              >
                המשך לתשלום
              </button>

              <Link
                href="/menu"
                className="block text-center mt-4 text-blue-600 hover:underline"
              >
                המשך קניה
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Edit Cart Item Modal */}
      {editingItem && (
        <EditCartItemModal
          cartItem={editingItem.item}
          cartIndex={editingItem.index}
          onSave={(index, item) => {
            updateCartItem(index, item);
            setEditingItem(null);
          }}
          onClose={() => setEditingItem(null)}
        />
      )}

      {/* Save to Favorites Modal */}
      {favoritesItem && (
        <SaveToFavoritesModal
          isOpen={true}
          onClose={() => setFavoritesItem(null)}
          productId={favoritesItem.productId}
          productName={favoritesItem.productName}
          productNameHe={favoritesItem.productNameHe || favoritesItem.productName}
          selectedOptions={favoritesItem.selectedOptions.reduce((acc, opt) => {
            const key = opt.optionId || opt.optionName;
            acc[key] = {
              optionId: opt.optionId,
              optionName: opt.optionName,
              optionNameHe: opt.optionNameHe,
              displayMode: opt.displayMode,
              selectedValue: opt.selectedValue,
              selectedValueHe: opt.selectedValueHe,
              quantity: opt.quantity,
              textValue: opt.textValue,
              priceModifier: opt.priceModifier,
            };
            return acc;
          }, {} as Record<string, any>)}
          specialInstructions={favoritesItem.specialInstructions}
        />
      )}
    </div>
  );
}
